import {
  Bug,
  CalendarClock,
  CircleCheck,
  CircleDashed,
  ClipboardList,
  Code,
  Compass,
  Ellipsis,
  FolderKanban,
  Gauge,
  GraduationCap,
  Hammer,
  Inbox,
  Keyboard,
  LifeBuoy,
  Lightbulb,
  ListTodo,
  type LucideIcon,
  Mail,
  Megaphone,
  MessagesSquare,
  Package,
  PenLine,
  Receipt,
  Scale,
  Search,
  ShieldCheck,
  ShoppingCart,
  Siren,
  Stamp,
  Tag,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { get_categories } from "../../api";
import type { ClassificationCategory, ProcessSummary } from "../../api/types";

/** The classification kinds a process can be grouped by. */
export type GroupingKind = "business_domain" | "process_pattern";

/** The group of processes with no classified case (analysed before classification). */
export const UNCLASSIFIED = "unclassified";

export type Categories = ReadonlyMap<string, ClassificationCategory>;

const SECONDS_FIELD = {
  business_domain: "businessDomainSeconds",
  process_pattern: "processPatternSeconds",
} as const;

/** The category covering most of the process's case time; `UNCLASSIFIED` when none does. */
export const processCategoryId = (process: ProcessSummary, kind: GroupingKind): string => {
  let best = UNCLASSIFIED;
  let bestSeconds = 0;
  for (const [id, seconds] of Object.entries(process[SECONDS_FIELD[kind]] ?? {})) {
    if (seconds > bestSeconds || (seconds === bestSeconds && best !== UNCLASSIFIED && id < best)) {
      best = id;
      bestSeconds = seconds;
    }
  }
  return best;
};

const NEUTRAL = "bg-slate-500/10 text-slate-700 dark:text-slate-300";

// Icons and tones for the default categories, by name; renamed or added ones get `Tag`.
const CATEGORY_LOOKS: Record<string, { icon: LucideIcon; tone: string }> = {
  "Finance & Accounting": {
    icon: Receipt,
    tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  },
  "Sales & Business Development": {
    icon: TrendingUp,
    tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  },
  Marketing: { icon: Megaphone, tone: "bg-pink-500/10 text-pink-700 dark:text-pink-300" },
  "Customer Service": {
    icon: LifeBuoy,
    tone: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  },
  "People & HR": { icon: Users, tone: "bg-orange-500/10 text-orange-700 dark:text-orange-300" },
  "Procurement & Vendors": {
    icon: ShoppingCart,
    tone: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300",
  },
  Legal: { icon: Scale, tone: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300" },
  "Compliance & Risk": {
    icon: ShieldCheck,
    tone: "bg-teal-500/10 text-teal-700 dark:text-teal-300",
  },
  "IT & Engineering": { icon: Code, tone: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300" },
  "Operations & Fulfillment": {
    icon: Package,
    tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  },
  "Strategy & Leadership": {
    icon: Compass,
    tone: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  },
  "Internal Coordination": {
    icon: MessagesSquare,
    tone: "bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300",
  },
  "Item processing": { icon: Inbox, tone: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300" },
  "Recurring cycle": {
    icon: CalendarClock,
    tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  },
  "Bespoke project": {
    icon: FolderKanban,
    tone: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  },
  "Unexpected event": { icon: Siren, tone: "bg-rose-500/10 text-rose-700 dark:text-rose-300" },
  Communicate: { icon: Mail, tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
  "Plan & Organize": {
    icon: ListTodo,
    tone: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  },
  "Research & Look Up": { icon: Search, tone: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300" },
  "Analyze & Assess": {
    icon: Lightbulb,
    tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  },
  "Report & Monitor": {
    icon: Gauge,
    tone: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
  },
  "Create Content": { icon: PenLine, tone: "bg-pink-500/10 text-pink-700 dark:text-pink-300" },
  "Enter & Process Data": {
    icon: Keyboard,
    tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  },
  "Check & Validate": {
    icon: CircleCheck,
    tone: "bg-teal-500/10 text-teal-700 dark:text-teal-300",
  },
  "Approve & Sign Off": {
    icon: Stamp,
    tone: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
  },
  "Investigate & Resolve Issues": {
    icon: Bug,
    tone: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  },
  "Configure & Build": {
    icon: Hammer,
    tone: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
  },
  Learn: { icon: GraduationCap, tone: "bg-lime-500/10 text-lime-700 dark:text-lime-300" },
  Admin: { icon: ClipboardList, tone: "bg-stone-500/10 text-stone-700 dark:text-stone-300" },
  Other: { icon: Ellipsis, tone: NEUTRAL },
};

/** How a category's group looks; `name` is empty for `UNCLASSIFIED` and unknown ids. */
export const categoryGroup = (id: string, categories: Categories) => {
  if (id === UNCLASSIFIED) return { name: "", icon: CircleDashed, tone: NEUTRAL };
  const category = categories.get(id);
  return {
    name: category?.name ?? "",
    ...(CATEGORY_LOOKS[category?.name ?? ""] ?? { icon: Tag, tone: NEUTRAL }),
  };
};

/** The category's display name, including the unclassified and unknown cases. */
export const categoryName = (id: string, categories: Categories, t: TFunction) =>
  id === UNCLASSIFIED
    ? t("process_directory.bucket_unclassified")
    : categories.get(id)?.name || t("process_directory.unknown_category");

/** One step type's share of the active time of some step occurrences. */
export type StepTypeShare = { id: string; seconds: number; pct: number };

/**
 * Active time per step type pooled over step occurrences, most first, with the time no type
 * accounts for (analyses that predate classification) as a trailing `UNCLASSIFIED` share. Empty
 * when nothing is classified.
 */
export const stepTypeShares = (
  occurrences: { duration: number; stepTypeSeconds?: Record<string, number> }[],
): StepTypeShare[] => {
  let total = 0;
  let classified = 0;
  const secondsById = new Map<string, number>();
  for (const occurrence of occurrences) {
    total += occurrence.duration;
    for (const [id, seconds] of Object.entries(occurrence.stepTypeSeconds ?? {})) {
      secondsById.set(id, (secondsById.get(id) ?? 0) + seconds);
      classified += seconds;
    }
  }
  if (classified <= 0) return [];
  const denom = Math.max(total, classified);
  const shares = [...secondsById]
    .map(([id, seconds]) => ({ id, seconds, pct: (seconds / denom) * 100 }))
    .sort((a, b) => b.seconds - a.seconds || a.id.localeCompare(b.id));
  const rest = denom - classified;
  const restPct = (rest / denom) * 100;
  return restPct >= 0.5 ? [...shares, { id: UNCLASSIFIED, seconds: rest, pct: restPct }] : shares;
};

/** Whether the occurrence spent time on step type `id`; `UNCLASSIFIED` matches one with none. */
export const hasStepType = (
  occurrence: { stepTypeSeconds?: Record<string, number> },
  id: string,
): boolean =>
  id === UNCLASSIFIED
    ? Object.keys(occurrence.stepTypeSeconds ?? {}).length === 0
    : (occurrence.stepTypeSeconds?.[id] ?? 0) > 0;

/** Whether `name` is a kind's catch-all, listed after the real categories. */
export const isOtherCategory = (name: string) => name === "Other";

/** The company's categories by id, archived ones included so old labels still resolve; `null`
 * while loading, empty when loading fails. */
export function useCategories(): Categories | null {
  const [categories, setCategories] = useState<Categories | null>(null);
  useEffect(() => {
    let live = true;
    get_categories()
      .then(rows => live && setCategories(new Map(rows.map(row => [row.id, row]))))
      .catch(() => live && setCategories(new Map()));
    return () => {
      live = false;
    };
  }, []);
  return categories;
}

// Placeholder copy deliberately stays separate from observed instance narratives.
export const PROCESS_DESCRIPTION_PLACEHOLDERS: Record<string, string> = {
  "outbound-pipeline-generation": "Research target accounts and prepare outbound outreach.",
  "internal-collaboration-admin": "Coordinate team updates, follow-ups, and shared work.",
  "attend-internal-meeting": "Join team meetings, share updates, and align on next steps.",
  "team-enablement-onboarding": "Prepare resources and support team onboarding.",
  "review-pipeline-lead-queue": "Review leads, opportunities, and pipeline progress.",
  "review-team-updates-and-announcements": "Catch up on team communications and announcements.",
  "invoice-approval": "Review supplier invoices and complete approval steps.",
  "message-teammate-slack": "Exchange information and coordinate with teammates.",
  "resolve-tooling-or-access-issue": "Configure applications and resolve access issues.",
  "manage-calendar-and-availability": "Schedule meetings and maintain calendar availability.",
};
